import { fabric } from 'fabric';
import { uniqueId } from 'lodash';
import Vec2 from 'vec2';

import { calculateEdgeConstraints } from './lengthConstraints';

import {
  CANVAS_OBJECT_TYPE_PREFIX,
  CORNER_RADIUS_SIGN,
} from '~/core/constants';
import { EditableLabel } from '~/core/engine/EditableLabel';
import { SideItemOption } from '~/core/enums';
import {
  AreaInfo,
  ArrowInfo,
  BumpOut,
  CornerInfo,
  CornerMap,
  DrawingEdge,
  DrawingShape,
  DrawingShapeProps,
  IDrawingShapeSaved,
  RemovedEdgeAndCorners,
  SideItem,
  Skeleton,
  SkeletonSeg,
} from '~/core/models';
import { DrawingAppliance } from '~/core/models/drawing-appliance.model';

export const cloneCornerMap = (cornerMap: CornerMap): CornerMap => {
  const clonedCornerMap: CornerMap = {};

  for (const key of Object.keys(cornerMap)) {
    clonedCornerMap[key] = cloneCornerInfo(cornerMap[key]);
  }

  return clonedCornerMap;
};

/**
 * clones a DrawingEdge
 * @param edge source to clone from
 * @param newShapeId if exists then is copying into a new shape
 * @param newEdgeId used for edgesAndCorners
 */
export const cloneEdge = (
  edge: DrawingEdge,
  newShapeId?: string,
  newEdgeId?: string
): DrawingEdge => {
  const edgeId = newShapeId ? (newEdgeId ?? uniqueId()) : edge.id;

  const clonedEdge = {
    ...edge,
    startDisplayVec: new Vec2(edge.startDisplayVec.x, edge.startDisplayVec.y),
    endDisplayVec: new Vec2(edge.endDisplayVec.x, edge.endDisplayVec.y),
    normalVec: new Vec2(edge.normalVec.x, edge.normalVec.y),
    dirVec: new Vec2(edge.dirVec.x, edge.dirVec.y),
    sideItem: cloneSideItem(edge.sideItem, edgeId),
    bumpOut: edge.bumpOut ? cloneBumpOut(edge.bumpOut) : undefined,
    id: edgeId,
    drawingShapeId: newShapeId ?? edge.drawingShapeId,
  };

  Object.defineProperty(clonedEdge, 'startDisplayVec', {
    value: clonedEdge.startDisplayVec,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(clonedEdge, 'endDisplayVec', {
    value: clonedEdge.endDisplayVec,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(clonedEdge, 'dirVec', {
    value: clonedEdge.dirVec,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(clonedEdge, 'normalVec', {
    value: clonedEdge.normalVec,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  return clonedEdge;
};

const cloneAppliance =
  (copying: boolean) =>
  (appliance: DrawingAppliance): DrawingAppliance => ({
    ...appliance,
    centerPoint: new Vec2(appliance.centerPoint.x, appliance.centerPoint.y),
    id: copying ? uniqueId() : appliance.id,
  });

const cloneBumpOut = (bumpOut: BumpOut): BumpOut => {
  const clonedBump: BumpOut = { ...DEFAULT_BUMPOUT, ...bumpOut };

  clonedBump.arrowsInfo = bumpOut.arrowsInfo.map((arrow) =>
    cloneArrowInfo(arrow)
  );

  return clonedBump;
};

export const cloneArrowInfo = (arrow: ArrowInfo): ArrowInfo => ({
  ...arrow,
  path: arrow.path.map((value) => new Vec2(value.x, value.y)),
});

const cloneEdgesAndCorners = (
  shape: DrawingShape,
  copying: boolean,
  newShape: DrawingShape
): RemovedEdgeAndCorners[] =>
  (shape.removedElementsHistory?.edgesAndCorners ?? []).map((sideItem) => {
    const foundIndex = shape.drawingEdges.findIndex(
      (drawingEdge) => drawingEdge.id === sideItem.drawingEdge.id
    );
    const newEdgeId = newShape.drawingEdges[foundIndex].id;
    const clonedArrowInfo = sideItem.arrowInfo
      ? cloneArrowInfo(sideItem.arrowInfo)
      : undefined;

    if (copying && clonedArrowInfo) {
      clonedArrowInfo.drawingEdgeId = newEdgeId;
    }

    const appliedCorners = cloneCornerArray(sideItem.appliedCorners) as [
      CornerInfo,
      CornerInfo,
    ];

    return {
      ...sideItem,
      sideItemType: sideItem.sideItemType,
      drawingEdge: cloneEdge(sideItem.drawingEdge, newShape.id, newEdgeId),
      arrowInfo: clonedArrowInfo,
      appliedCorners,
    };
  });

const cloneSkeletonSegment = (segment: SkeletonSeg): SkeletonSeg => ({
  ...segment,
  pos: new Vec2(segment.pos.x, segment.pos.y),
});

/**
 * cloneAreaInfo is trivial but consistent with the other clone functions
 * @param area
 */
const cloneAreaInfo = (area: AreaInfo): AreaInfo => ({
  ...area,
});

/**
 * cloneDrawingShape is used for making a copy of a shape when a user requests this, for
 * saving shapes in an undo/redo stack for those functions, and for creating a 'whatif'
 * version in memory to do recursive projections on edge sizes in order to find optimal
 * constraint values.
 * @param originalShape is the DrawingShape we will be cloning here
 * @param isCopying is passed down to more granular clone routines to control whether
 *          new unique id's need to be created for various objects.  This will be
 *          false for the case where we're cloning for the undo/redo stack.
 * @param skipConstraints is checked here to prevent an infinite recursive loop.
 *         If we’re cloning a shape to create another shape for the user,
 *         then we DO want to calculate the edge constraints for the new shape
 *         If we are cloning a shape just to have something in memory to do whatIf’s
 *         on, we’re already called the high level calculate edge constraints and
 *         need only refine them with the refine routine.
 */
export const cloneDrawingShape =
  ({
    isCopying,
    skipConstraints,
  }: {
    isCopying: boolean;
    skipConstraints: boolean;
  }) =>
  (originalShape: DrawingShape): DrawingShape => {
    // this ensures the new object is a DrawingShape with all methods
    const clonedShape = Object.assign(
      createDrawingShape(originalShape),
      originalShape
    ); // TODO Is the assign still necessary?  I don't think so.

    clonedShape.id = isCopying ? uniqueId() : originalShape.id;
    clonedShape.arrowsInfo = originalShape.arrowsInfo?.map((arrow) =>
      cloneArrowInfo(arrow)
    );
    clonedShape.areaInfo = originalShape.areaInfo
      ? cloneAreaInfo(originalShape.areaInfo)
      : undefined;
    clonedShape.onRename = originalShape.onRename;
    clonedShape.cornerMap = cloneCornerMap(originalShape.cornerMap);
    clonedShape.drawingEdges = originalShape.drawingEdges.map((edge) =>
      cloneEdge(edge, clonedShape.id, isCopying ? undefined : edge.id)
    );
    clonedShape.appliances = originalShape.appliances.map(
      cloneAppliance(isCopying)
    );
    clonedShape.skeleton = originalShape.skeleton.map((seg) =>
      cloneSkeletonSegment(seg)
    );
    clonedShape.fabricGroup = isCopying
      ? new fabric.Group()
      : originalShape.fabricGroup;
    clonedShape.canvasObjectName = isCopying
      ? CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN + '_' + uniqueId()
      : originalShape.canvasObjectName;
    clonedShape.counterLabel = isCopying
      ? undefined
      : originalShape.counterLabel;
    clonedShape.label = isCopying ? undefined : originalShape.label;

    if (isCopying) {
      clonedShape.arrowsInfo?.forEach((_arrow, index) => {
        if (clonedShape.arrowsInfo) {
          clonedShape.arrowsInfo[index] = {
            ...clonedShape.arrowsInfo[index],
            drawingEdgeId: clonedShape?.drawingEdges[index]?.id,
          };
        }
      });
      // this fixes duplicate shapes being unreachable with the mouse clicks
      clonedShape.fabricGroup.set({ subTargetCheck: true });
    }

    clonedShape.removedElementsHistory = {
      edgesAndCorners: cloneEdgesAndCorners(
        originalShape,
        isCopying,
        clonedShape
      ),
    };
    clonedShape.establishedLetterStartIndex =
      originalShape.establishedLetterStartIndex;

    if (!skipConstraints) {
      calculateEdgeConstraints(clonedShape);
    }

    return clonedShape;
  };

/**
     * return a list of SimpleLines connecting all points of the highlightPoints
     * last point connects to first point
     *
     getPolygonLines(): Array<SimpleLine> | undefined {
        if (!this.highlightPoints) return;
        return getLinesFromPolygonPoints(this.highlightPoints);
    }*/

const cloneCornerArray = (cornerArray: CornerInfo[]): CornerInfo[] =>
  cornerArray.map((corner) => cloneCornerInfo(corner));

//TODO: seems is unused
export const toSaved = (shape: DrawingShape): IDrawingShapeSaved => ({
  skeleton: shape.skeleton,
  cornerMap: shape.cornerMap,
  type: shape.type,
  appliances: shape.appliances,
  edge: shape.edge,
});

/**
 * EditableLabel is a class, so unfortunately, refactoring this class to eliminate mutation is not straightforward.
 */
export const darkenAllLabels = (editableLabels: EditableLabel[]): void => {
  editableLabels.forEach((editableLabel) => {
    editableLabel.darken();
  });
};

/**
 * EditableLabel is a class, so unfortunately, refactoring this class to eliminate mutation is not straightforward.
 */
export const removeLabelLetters = (editableLabels: EditableLabel[]): void => {
  editableLabels.forEach((editableLabel) => {
    if (!editableLabel.labelText.text?.includes(CORNER_RADIUS_SIGN)) {
      editableLabel.labelText.text = '';
    }
  });
};

/**
 * EditableLabel is a class, so unfortunately, refactoring this class to eliminate mutation is not straightforward.
 */
export const lightenAllLabels = (editableLabels: EditableLabel[]): void => {
  editableLabels.forEach((editableLabel) => {
    editableLabel.lighten();
  });
};

/**
 * EditableLabel is a class, so unfortunately, refactoring this class to eliminate mutation is not straightforward.
 */
export const hideAllLabels = (editableLabels: EditableLabel[]): void => {
  editableLabels.forEach((editableLabel) => {
    editableLabel.hide();
  });
};

export const getJointPointsCount = (skeleton: Skeleton): number =>
  // Ignores the first and last skeleton points
  skeleton.length > 2 ? skeleton.length - 2 : 0;

export const createDrawingShape = (props: DrawingShapeProps): DrawingShape => {
  const skeleton = props.skeleton;
  const cornerMap = props.cornerMap ?? {};
  const drawingEdges = props.drawingEdges ?? [];
  const type = props.type;
  const id = props.id ?? '';
  const fabricGroup = props.fabricGroup ?? new fabric.Group();

  fabricGroup.set({
    subTargetCheck: true,
  });
  const editableLabels = props.editableLabels ?? [];
  const appliances = props.appliances ? [...props.appliances] : [];
  const edge = props.edge ?? undefined;

  return {
    skeleton,
    cornerMap,
    drawingEdges,
    type,
    id,
    fabricGroup,
    editableLabels,
    appliances,
    edge,
    // Optional properties
    areaInfo: props.areaInfo,
    arrowsInfo: props.arrowsInfo,
    canvasObjectName: props.canvasObjectName,
    counterLabel: props.counterLabel,
    establishedLetterStartIndex: props.establishedLetterStartIndex,
    highlightPoints: props.highlightPoints,
    label: props.label,
    onRename: props.onRename,
    removedElementsHistory: props.removedElementsHistory,

    toJSON() {
      // onTextChange will result in cyclical errors
      // fabricGroup has no meaningful information to save
      return { ...this, onTextChange: undefined, fabricGroup: undefined };
    },
  };
};

export const cloneSideItem = (source: SideItem, edgeId?: string): SideItem => {
  const newSideItem = Object.assign<SideItem, SideItem>(
    { ...DEFAULT_SIDE_ITEM },
    source
  );

  newSideItem.arrowsInfo = source.arrowsInfo.map((arrow) => {
    const newArrowInfo = cloneArrowInfo(arrow);

    if (edgeId) {
      newArrowInfo.drawingEdgeId = edgeId;
    }

    return newArrowInfo;
  });

  return newSideItem;
};

export const DEFAULT_SIDE_ITEM = {
  depth: 0,
  arrowsInfo: [] satisfies ArrowInfo[],
  type: undefined,
} as const satisfies SideItem;

export const createSideItem = (
  depth?: number,
  arrowsInfo?: Array<ArrowInfo>,
  type?: SideItemOption
): SideItem => ({
  depth: depth || DEFAULT_SIDE_ITEM.depth,
  arrowsInfo: arrowsInfo || DEFAULT_SIDE_ITEM.arrowsInfo,
  type: type || DEFAULT_SIDE_ITEM.type,
});

export const DEFAULT_BUMPOUT = {
  depth: 0,
  length: 0,
  counterClockwiseRemainderLength: 0,
  clockwiseRemainderLength: 0,
  arrowsInfo: [] satisfies ArrowInfo[],
} as const satisfies BumpOut;

export const cloneCornerInfo = (source: CornerInfo): CornerInfo => ({
  ...source,
  coords: new Vec2(source.coords.x, source.coords.y),
  labelText: fabric.util.object.clone(source.labelText),
  labelMeasure: fabric.util.object.clone(source.labelMeasure),
  radiusCenterPoint: source.radiusCenterPoint
    ? new Vec2(source.radiusCenterPoint.x, source.radiusCenterPoint.y)
    : undefined,
  replacementStartCoords: source.replacementStartCoords
    ? new Vec2(source.replacementStartCoords.x, source.replacementStartCoords.y)
    : undefined,
  replacementEndCoords: source.replacementEndCoords
    ? new Vec2(source.replacementEndCoords.x, source.replacementEndCoords.y)
    : undefined,
});

export const createCornerInfo = (
  id: string,
  coords: Vec2,
  skIndex: number,
  isUpper: boolean
): CornerInfo => ({
  id,
  coords,
  skIndex,
  isUpper,
  label: '',
  angle: 0,
  toJSON() {
    // just take the text of the fabric labels
    return {
      ...this,
      labelText: this.labelText?.text,
      labelMeasure: this.labelMeasure?.text,
    };
  },
});
